import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useTranslation } from 'react-i18next';
import PrimaryModal from 'components/common/PrimaryModal/PrimaryModal';
import styled from '@emotion/styled';
import { TextStyle } from 'styles/variables/mixins';
import { Template } from './TemplateDropdownWithActions';
import WarningIcon from '@mui/icons-material/Warning';
import { Box } from '@mui/material';

interface TemplateDeleteConfirmationProps {
    open: boolean;
    template: Template | null;
    isCurrentlySelected: boolean;
    onClose: () => void;
    onConfirm: () => void;
    mobile?: boolean;
}

const SaveButton = styled.button<{ mobile?: boolean; variant?: 'primary' | 'danger' }>`
    flex: 1 1 0;
    width: 180px;
    height: 36px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    ${TextStyle({ fontSize: '14px', fontWeight: '400' })}
    background: ${(props) => (props.variant === 'danger' ? '#d32f2f' : 'white')};
    color: ${(props) => (props.variant === 'danger' ? 'white' : '#8353E2FF')};
    opacity: 1;
    border: 1px solid ${(props) => (props.variant === 'danger' ? '#d32f2f' : '#8353e2ff')};
    border-radius: 3px;
    cursor: pointer;

    &:hover {
        background: ${(props) => (props.variant === 'danger' ? '#b71c1c' : '#6025d8ff')};
        color: #ffffffff;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    width: ${(props) => (props.mobile ? '137px !important' : '180px')};
`;

const Buttons = styled.div`
    margin: auto;
    margin-top: 30px;
    display: flex;
    justify-content: space-around;
    gap: 22px;
`;

const Text = styled.p`
    ${TextStyle({ fontSize: '14px', fontWeight: '400', color: '#565E6C' })}
    margin-top: 25px;
    text-align: center;
`;

const WarningBox = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 12px;
    background-color: #fff3e0;
    border-left: 4px solid #ff9800;
    border-radius: 4px;
`;

const WarningText = styled.p`
    ${TextStyle({ fontSize: '13px', fontWeight: '400', color: '#e65100' })}
    margin: 0;
`;

const InfoText = styled.p`
    ${TextStyle({ fontSize: '13px', fontWeight: '400', color: '#1976d2' })}
    margin-top: 12px;
    text-align: center;
    font-style: italic;
`;

const TemplateDeleteConfirmation = ({
    open,
    template,
    isCurrentlySelected,
    onClose,
    onConfirm,
    mobile,
}: TemplateDeleteConfirmationProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();

    return (
        <PrimaryModal
            style={{
                top: '32%',
                transform: 'translate(-50%, 0)',
                padding: '0px',
                borderRadius: '10px',
            }}
            maxWidth="500px"
            open={open}
            onClose={onClose}
        >
            <div style={{ padding: '18px 34px' }}>
                <h3 className="text-center bold text-black">
                    {t('deleteTemplate', { lng: language }) || 'Delete Template'}
                </h3>

                {isCurrentlySelected ? (
                    <>
                        <WarningBox>
                            <WarningIcon style={{ color: '#ff9800' }} />
                            <WarningText>
                                {t('cannotDeleteSelectedTemplate', { lng: language }) ||
                                    'Cannot delete the currently selected template. Please select a different template first.'}
                            </WarningText>
                        </WarningBox>
                        <Text>
                            {t('templateName', { lng: language }) || 'Template'}:{' '}
                            <strong>{template?.name}</strong>
                        </Text>
                    </>
                ) : (
                    <>
                        <Text>
                            {t('confirmDeleteTemplate', { lng: language }) ||
                                'Are you sure you want to delete this template?'}
                        </Text>
                        <Text>
                            <strong>{template?.name}</strong>
                        </Text>
                        <InfoText>
                            {t('existingAgreementsUnaffected', { lng: language }) ||
                                'Existing agreements will not be affected.'}
                        </InfoText>
                    </>
                )}

                <Buttons>
                    <SaveButton onClick={onClose} mobile={mobile}>
                        {isCurrentlySelected
                            ? t('close', { lng: language }) || 'Close'
                            : t('cancel', { lng: language }) || 'Cancel'}
                    </SaveButton>

                    {!isCurrentlySelected && (
                        <SaveButton onClick={onConfirm} mobile={mobile} variant="danger">
                            {t('deleteTemplate', { lng: language }) || 'Delete'}
                        </SaveButton>
                    )}
                </Buttons>
            </div>
        </PrimaryModal>
    );
};

export default TemplateDeleteConfirmation;
